import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiPageResponse } from '../types/api.types';
import {
  CreateWithdrawalForPrestataireRequest,
  WithdrawRequest,
  WithdrawalRequestView,
  WithdrawalStatus,
} from '../types/withdrawal.types';

/**
 * Retraits BALANCE (#57) — `/wallet/api/withdrawals/*`. Couche d'approbation
 * (opérateur/admin) + seuils au-dessus de l'exécution GUTouch sortante.
 *
 * Le gel de la BALANCE est immédiat à la création (le solde retirable baisse) ;
 * le rejet re-crédite. Fichier **identique** entre les 3 apps ; chaque app
 * n'utilise que le sous-ensemble de méthodes correspondant à ses rôles.
 */
@Injectable({ providedIn: 'root' })
export class WithdrawalsService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/wallet/api/withdrawals`;

  /** Le titulaire courant retire son propre solde BALANCE. */
  createSelf(
    ownerType: 'OWNER' | 'OPERATOR',
    body: WithdrawRequest,
  ): Observable<WithdrawalRequestView> {
    const params = new HttpParams().set('ownerType', ownerType);
    return this.http.post<WithdrawalRequestView>(`${this.base}/me`, body, { params });
  }

  /** Un opérateur déclenche un retrait pour un prestataire qu'il gère. */
  createForPrestataire(
    body: CreateWithdrawalForPrestataireRequest,
  ): Observable<WithdrawalRequestView> {
    return this.http.post<WithdrawalRequestView>(`${this.base}/for-prestataire`, body);
  }

  // ── Décisions ────────────────────────────────────────────────────────────
  operatorApprove(id: number): Observable<WithdrawalRequestView> {
    return this.http.post<WithdrawalRequestView>(`${this.base}/${id}/operator-approve`, {});
  }
  operatorReject(id: number, reason?: string): Observable<WithdrawalRequestView> {
    return this.http.post<WithdrawalRequestView>(`${this.base}/${id}/operator-reject`, {
      reason: reason ?? null,
    });
  }
  adminApprove(id: number): Observable<WithdrawalRequestView> {
    return this.http.post<WithdrawalRequestView>(`${this.base}/admin/${id}/approve`, {});
  }
  adminReject(id: number, reason?: string): Observable<WithdrawalRequestView> {
    return this.http.post<WithdrawalRequestView>(`${this.base}/admin/${id}/reject`, {
      reason: reason ?? null,
    });
  }

  // ── Listes (enveloppe Nawam, 1-indexée) ───────────────────────────────────
  /** Mes demandes (prestataire ou opérateur) — `GET /me`. */
  listMine(page = 1, size = 20): Observable<ApiPageResponse<WithdrawalRequestView>> {
    const params = new HttpParams().set('page', page).set('size', size);
    return this.http.get<ApiPageResponse<WithdrawalRequestView>>(`${this.base}/me`, { params });
  }

  /** Inbox opérateur — `GET /operator?status=`. */
  listForOperator(
    status: WithdrawalStatus | null,
    page = 1,
    size = 20,
  ): Observable<ApiPageResponse<WithdrawalRequestView>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (status) params = params.set('status', status);
    return this.http.get<ApiPageResponse<WithdrawalRequestView>>(`${this.base}/operator`, {
      params,
    });
  }

  /** Inbox admin — `GET /admin?status=`. */
  listForAdmin(
    status: WithdrawalStatus | null,
    page = 1,
    size = 20,
  ): Observable<ApiPageResponse<WithdrawalRequestView>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (status) params = params.set('status', status);
    return this.http.get<ApiPageResponse<WithdrawalRequestView>>(`${this.base}/admin`, {
      params,
    });
  }
}
